import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Check, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { FormField } from "~/components/ui/form-field";
import { Textarea } from "~/components/ui/textarea";
import { useQuickMatchContext } from "~/lib/discovery/quick-match-context";
import { updateDiscoveryState } from "~/lib/discovery/state";
import { processQuickMatch } from "~/lib/functions/quick-match";
import { personalContextField } from "~/lib/schemas/coach-discovery";

// ---------------------------------------------------------------------------
// Schema for this step only
// ---------------------------------------------------------------------------

const ContextStepSchema = z.object({
  personalContext: z
    .string()
    .trim()
    .min(1, "Please describe what you're looking for.")
    .max(500, "Your description must be 500 characters or less."),
});

type ContextStepForm = z.infer<typeof ContextStepSchema>;

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/_public/discovery/match/context")({
  component: QuickMatchContext,
});

// ---------------------------------------------------------------------------
// Processing states
// ---------------------------------------------------------------------------

const PROCESSING_STEPS = [
  "Analyzing your goals...",
  "Finding matching coaches...",
  "Ranking your results...",
] as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function QuickMatchContext() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state: quickMatchState } = useQuickMatchContext();
  const runQuickMatch = useServerFn(processQuickMatch);

  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState(0);
  const processingStarted = useRef(false);

  const { handleSubmit, control, formState } = useForm<ContextStepForm>({
    resolver: zodResolver(ContextStepSchema),
    defaultValues: { personalContext: "" },
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  const { errors, isValid } = formState;

  // Step through processing messages on a timer
  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(() => {
      setProcessingStep((prev) =>
        prev < PROCESSING_STEPS.length - 1 ? prev + 1 : prev,
      );
    }, 1200);
    return () => clearInterval(interval);
  }, [isProcessing]);

  const onSubmit = useCallback(
    async (data: ContextStepForm) => {
      if (!quickMatchState.goals?.length) {
        toast.error("Please go back and select your goals first.");
        navigate({ to: "/discovery/match" });
        return;
      }

      if (processingStarted.current) return;
      processingStarted.current = true;

      setIsProcessing(true);

      try {
        const result = await runQuickMatch({
          data: {
            goals: quickMatchState.goals,
            otherGoal: quickMatchState.otherGoal,
            personalContext: data.personalContext,
          },
        });

        if (result.status === "error") {
          toast.error(result.message);
          setIsProcessing(false);
          processingStarted.current = false;
          return;
        }

        if (result.status === "not_found" || result.matches.length === 0) {
          toast.error(
            "We couldn't find matching coaches right now. Please try again later.",
          );
          setIsProcessing(false);
          processingStarted.current = false;
          return;
        }

        // Persist to localStorage so the refine flow can read goals + context
        updateDiscoveryState({
          goals: quickMatchState.goals,
          otherGoal: quickMatchState.otherGoal,
          personalContext: data.personalContext,
        });

        // Store matches in query cache for the results page
        queryClient.setQueryData(["quickMatchResults"], result.matches);

        // Brief delay so the last processing step is visible
        await new Promise((r) => setTimeout(r, 600));

        navigate({ to: "/discovery/results" });
      } catch {
        toast.error("Something went wrong. Please try again.");
        setIsProcessing(false);
        processingStarted.current = false;
      }
    },
    [quickMatchState, runQuickMatch, queryClient, navigate],
  );

  // Processing overlay
  if (isProcessing) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <motion.div
          className="flex size-20 items-center justify-center rounded-full bg-brand-50"
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <Loader2 className="size-8 animate-spin text-brand-600" />
        </motion.div>

        <div className="mt-8 min-h-[4rem] text-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={processingStep}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="text-lg font-medium text-gray-700"
            >
              {PROCESSING_STEPS[processingStep]}
            </motion.p>
          </AnimatePresence>
        </div>

        <div className="mt-6 flex flex-col items-center gap-2">
          {PROCESSING_STEPS.map((step, i) => (
            <motion.div
              key={step}
              className="flex items-center gap-2 text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: i <= processingStep ? 1 : 0.3 }}
              transition={{ delay: i * 0.1 }}
            >
              {i <= processingStep ? (
                <Check className="size-4 text-green-600" />
              ) : (
                <div className="size-4" />
              )}
              <span
                className={
                  i <= processingStep ? "text-gray-700" : "text-gray-400"
                }
              >
                {step}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
      <DiscoveryStep.Title title="Tell us what you're looking for" />
      <DiscoveryStep.SubTitle title="Describe your situation, challenges, or goals in your own words. This helps our AI find the most relevant coaches." />

      <DiscoveryStep.Card className="space-y-6">
        <FormField error={errors.personalContext?.message}>
          <Controller
            name="personalContext"
            control={control}
            render={({ field }) => (
              <Textarea
                value={field.value}
                onValueChange={field.onChange}
                className="min-h-[140px]"
                maxLength={personalContextField.maxLength}
                placeholder={personalContextField.placeholder}
              />
            )}
          />
        </FormField>
      </DiscoveryStep.Card>

      <div className="mt-6 flex justify-between">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate({ to: "/discovery/match" })}
        >
          Back
        </Button>
        <Button type="submit" disabled={!isValid} variant="primary">
          Find My Matches
        </Button>
      </div>
    </form>
  );
}
